import { Injectable, DestroyRef, inject, effect, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { AuthService } from '@modules/auth/services/auth.service';
import { ConversationsSseService, SseEvent } from '@modules/conversations/services/conversations-sse.service';
import { ConversationsApiService } from '@modules/conversations/services/conversations-api.service';
import { ConversationsService } from '@modules/conversations/services/conversations.service';

/**
 * Manages SSE lifecycle tied to authentication state.
 * Connects on login, disconnects on logout.
 * Shows toast notifications for incoming SSE events.
 * Fetches initial unread count on connect/reconnect.
 */
@Injectable({
  providedIn: 'root',
})
export class SseNotificationService {
  private readonly authService = inject(AuthService);
  private readonly sseService = inject(ConversationsSseService);
  private readonly conversationsService = inject(ConversationsService);
  private readonly api = inject(ConversationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // React to auth state changes
    this.authService.isAuthenticated$.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.sseService.connect();
        this.fetchUnreadCount();
      } else {
        this.sseService.disconnect();
      }
    });

    // Show toast notifications on SSE events
    effect(() => {
      const event = this.sseService.lastEvent();
      if (event) {
        untracked(() => this.showNotification(event));
      }
    });

    // Refetch unread count on reconnect
    effect(() => {
      const connected = this.sseService.connected();
      if (connected) {
        untracked(() => this.fetchUnreadCount());
      }
    });
  }

  private fetchUnreadCount(): void {
    this.api.getUnreadCount().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => this.sseService.setUnreadCount(res.totalUnread),
    });
  }

  private showNotification(event: SseEvent): void {
    // Don't show notification if the user is currently viewing this thread
    const selectedThreadId = this.conversationsService.selectedThread()?.id;
    if (event.threadId && event.threadId === selectedThreadId) {
      return;
    }

    switch (event.type) {
      case 'inquiry.new': {
        const guestName = event.guestName ?? '';
        const propertyName = event.propertyName ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.new'),
          detail: this.transloco.translate('notifications.inquiry.newDetail', { guestName, propertyName }),
          life: 5000,
        });
        break;
      }

      case 'inquiry.new_message':
      case 'conversation.new_message': {
        // Only notify about guest messages (not host's own messages)
        if (event.senderType === 'host') return;

        const guestName = event.guestName ?? '';
        const preview = event.messagePreview ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.newMessage'),
          detail: guestName
            ? this.transloco.translate('notifications.inquiry.newMessageDetail', { guestName, preview })
            : preview,
          life: 5000,
        });
        break;
      }

      case 'conversation.new': {
        const guestName = event.guestName ?? '';
        this.messageService.add({
          severity: 'info',
          summary: this.transloco.translate('notifications.inquiry.new'),
          detail: this.transloco.translate('notifications.inquiry.newDetail', {
            guestName,
            propertyName: event.propertyName ?? '',
          }),
          life: 5000,
        });
        break;
      }

      case 'inquiry.closed':
      case 'conversation.closed': {
        const guestName = event.guestName ?? '';
        this.messageService.add({
          severity: 'warn',
          summary: this.transloco.translate('notifications.inquiry.closed'),
          detail: guestName
            ? this.transloco.translate('notifications.inquiry.closedDetail', { guestName })
            : '',
          life: 4000,
        });
        break;
      }

      // Presence events don't need toast notifications
      default:
        break;
    }
  }
}
